import { useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Printer, X } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  useCancelInvoice,
  useCreateInvoice,
  useInvoiceSummary,
  useInvoices,
  useIssueInvoice,
  useRecordInvoicePayment,
} from '@/hooks/use-invoices';
import { useExportInvoicePdf } from '@/hooks/use-pdf-export';
import { cn } from '@/lib/cn';
import { fmtCurrency as fmt, fmtDate as formatDate } from '@/lib/format';
import { toast } from '@/stores/toast-store';

interface InvoiceTabProps {
  loanId: string;
  loanNumber: string;
}

const INVOICE_TYPES = [
  { value: 'ESTABLISHMENT_FEE', label: 'Establishment Fee' },
  { value: 'BROKERAGE_FEE', label: 'Brokerage Fee' },
  { value: 'EXTENSION_FEE', label: 'Extension Fee' },
  { value: 'LEGAL_COSTS', label: 'Legal Costs' },
  { value: 'MANAGEMENT_FEE', label: 'Management Fee' },
  { value: 'DEFAULT_INTEREST', label: 'Default Interest' },
  { value: 'OTHER', label: 'Other' },
];

const INVOICE_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700' },
  ISSUED: { bg: 'bg-blue-100', text: 'text-blue-700' },
  PAID: { bg: 'bg-green-100', text: 'text-green-700' },
  OVERDUE: { bg: 'bg-red-100', text: 'text-red-700' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-400 line-through' },
};

const invoiceDateFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatInvoiceDate(value: string | Date | null | undefined) {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : invoiceDateFormatter.format(date);
}

function isInvoiceOverdue(dueDate: string | Date | null | undefined) {
  if (!dueDate) return false;
  const date = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
  if (Number.isNaN(date.getTime())) return false;

  const dueDay = date.toISOString().slice(0, 10);
  const todayUtc = new Date().toISOString().slice(0, 10);
  return dueDay < todayUtc;
}

export function InvoiceTab({ loanId, loanNumber: _loanNumber }: InvoiceTabProps) {
  const queryClient = useQueryClient();
  const { data: invoices, isLoading } = useInvoices(loanId);
  const { data: summary } = useInvoiceSummary(loanId);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ type: '', amount: '', dueDate: '' });
  const [typeError, setTypeError] = useState('');
  const [amountError, setAmountError] = useState('');

  const createInvoice = useCreateInvoice();
  const issueInvoice = useIssueInvoice();
  const recordPayment = useRecordInvoicePayment();
  const cancelInvoice = useCancelInvoice();
  const exportInvoicePdf = useExportInvoicePdf();

  const invalidateInvoiceData = () => {
    queryClient.invalidateQueries({ queryKey: ['invoices', loanId] });
    queryClient.invalidateQueries({ queryKey: ['invoice-summary', loanId] });
  };

  const sanitizeAmountInput = (value: string) =>
    value.replace(/[^\d.]/g, '').replace(/^0+(?=\d)/, '');

  const handleCreateInvoice = async () => {
    const nextTypeError = formData.type ? '' : 'Type is required.';
    const nextAmountError = !formData.amount ? 'Amount is required.' : '';
    setTypeError(nextTypeError);
    setAmountError(nextAmountError);

    if (nextTypeError || nextAmountError) {
      toast.error('Please fill in required fields');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setAmountError('Amount must be greater than 0.');
      return;
    }

    const gst = amount * 0.1; // 10% GST
    const total = amount + gst;

    createInvoice.mutate(
      {
        loanId,
        type: formData.type,
        amount,
        gst,
        total,
        ...(formData.dueDate ? { dueDate: formData.dueDate } : {}),
      },
      {
        onSuccess: () => {
          invalidateInvoiceData();
          toast.success('Invoice created');
          setFormData({ type: '', amount: '', dueDate: '' });
          setTypeError('');
          setAmountError('');
          setShowCreateForm(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading invoices…
      </div>
    );
  }

  const invoiceList = Array.isArray(invoices)
    ? invoices
    : Array.isArray(invoices?.data)
      ? invoices.data
      : Array.isArray((invoices as any)?.invoices)
        ? (invoices as any).invoices
        : [];

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Total Invoiced</p>
            <p className="text-lg font-semibold text-gray-900">{fmt(summary.totalInvoiced)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Total Paid</p>
            <p className="text-lg font-semibold text-green-600">{fmt(summary.totalPaid)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Outstanding</p>
            <p className="text-lg font-semibold text-red-600">{fmt(summary.outstanding)}</p>
          </div>
        </div>
      )}

      {/* Create button */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-900">Invoices</h3>
        <Button
          variant="primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="w-[160px] shrink-0 justify-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Invoice
        </Button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={e => {
                  setTypeError('');
                  setFormData({ ...formData, type: e.target.value });
                }}
                className={cn(
                  'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  typeError ? 'border-red-300 bg-red-50 focus:ring-red-500' : 'border-gray-200',
                )}
              >
                <option value="">Select type</option>
                {INVOICE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {typeError && <p className="mt-1 text-sm text-red-600">{typeError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (AUD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={formData.amount}
                onChange={e => {
                  setAmountError('');
                  setFormData({ ...formData, amount: sanitizeAmountInput(e.target.value) });
                }}
                onBlur={() => {
                  if (!formData.amount) {
                    setAmountError('Amount is required.');
                    return;
                  }

                  const amount = parseFloat(formData.amount);
                  if (!Number.isFinite(amount) || amount <= 0) {
                    setAmountError('Amount must be greater than 0.');
                  }
                }}
                className={cn(
                  'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  amountError ? 'border-red-300 bg-red-50 focus:ring-red-500' : 'border-gray-200',
                )}
                placeholder="0.00"
              />
              {amountError && <p className="mt-1 text-sm text-red-600">{amountError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {formData.amount && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Incl. GST</label>
                <div className="px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-700 bg-gray-50">
                  {fmt((parseFloat(formData.amount) || 0) * 1.1)}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowCreateForm(false)} className="w-fit">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateInvoice}
              disabled={createInvoice.isPending}
              className="w-fit"
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {/* Invoice list */}
      {invoiceList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 bg-white border border-gray-200 rounded-lg">
          <FileText className="w-8 h-8 mb-2" />
          <p className="text-sm">No invoices yet</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Number</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Type</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Amount</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">GST</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Total</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Issued</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Due</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoiceList.map((inv: any) => {
                const statusColor =
                  INVOICE_STATUS_COLORS[inv.status] || INVOICE_STATUS_COLORS.DRAFT;
                const isOverdue = inv.status === 'ISSUED' && isInvoiceOverdue(inv.dueDate);

                return (
                  <tr
                    key={inv.id}
                    className={cn('hover:bg-gray-50 transition-colors', isOverdue && 'bg-red-50')}
                  >
                    <td className="px-4 py-3 font-mono font-medium text-gray-900">
                      {inv.invoiceNumber ?? inv.number ?? '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {INVOICE_TYPES.find(t => t.value === inv.type)?.label || inv.type}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'text-xs font-medium px-2 py-1 rounded-full',
                          statusColor.bg,
                          statusColor.text,
                        )}
                      >
                        {isOverdue ? 'OVERDUE' : inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {fmt(inv.amount)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {fmt(inv.gst)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {fmt(inv.totalAmount ?? inv.total ?? 0)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {inv.issuedDate ? formatDate(inv.issuedDate) : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatInvoiceDate(inv.dueDate)}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {inv.status === 'DRAFT' && (
                        <button
                          onClick={() =>
                            issueInvoice.mutate(inv.id, {
                              onSuccess: () => {
                                invalidateInvoiceData();
                                toast.success('Invoice issued');
                              },
                            })
                          }
                          className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          Issue
                        </button>
                      )}
                      {inv.status === 'ISSUED' && (
                        <button
                          onClick={() =>
                            recordPayment.mutate(
                              {
                                id: inv.id,
                                amount: inv.totalAmount ?? inv.total ?? 0,
                                ref: inv.invoiceNumber ?? inv.number ?? inv.id,
                              },
                              {
                                onSuccess: () => {
                                  invalidateInvoiceData();
                                  toast.success('Payment recorded');
                                },
                              },
                            )
                          }
                          className="text-xs text-green-600 hover:text-green-700 font-medium"
                        >
                          Pay
                        </button>
                      )}
                      <button
                        onClick={() => exportInvoicePdf.mutate(inv.id)}
                        disabled={exportInvoicePdf.isPending}
                        className="text-xs text-gray-600 hover:text-gray-700 font-medium disabled:opacity-50"
                        title="Print invoice"
                      >
                        <Printer className="w-3.5 h-3.5 inline" />
                      </button>
                      {inv.status !== 'PAID' && (
                        <button
                          onClick={() => {
                            if (confirm('Cancel this invoice?')) {
                              cancelInvoice.mutate(inv.id, {
                                onSuccess: () => {
                                  invalidateInvoiceData();
                                  toast.success('Invoice cancelled');
                                },
                              });
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          <X className="w-3.5 h-3.5 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
